/**
 * Zod validation schemas for node configs.
 *
 * Mirrors backend/app/schemas/node_configs.py exactly.
 * Used for client-side validation before saving config changes.
 *
 * Usage:
 *   import { validateNodeConfig } from '@/lib/types/node-configs'
 *   const errors = validateNodeConfig('llm_text', configObj)
 */

import { z } from "zod"

// ── Shared sub-schemas ──────────────────────────────────────────────────────

const structuredOutputFieldSchema = z.object({
  name: z.string().min(1),
  type: z.enum(["string", "number", "boolean"]).default("string"),
})

const assetRefSchema = z.object({
  storageKey: z.string(),
})

// ── Generation nodes ────────────────────────────────────────────────────────

export const llmTextConfigSchema = z.object({
  provider: z.string().default("google"),
  model: z.string().default("gemini-2.5-pro"),
  prompt: z.string().default(""),
  system_prompt: z.string().nullish(),
  temperature: z.coerce.number().min(0).max(2).nullish(),
  max_tokens: z.coerce.number().int().min(1).max(2_000_000).nullish(),
  output_schema: z.array(structuredOutputFieldSchema).nullish(),
  brand_kit_enabled: z.boolean().nullish(),
})
export type LLMTextConfig = z.infer<typeof llmTextConfigSchema>

export const imageGenConfigSchema = z.object({
  provider: z.string().default("google"),
  model: z.string().default("gemini-3.1-flash-image-preview"),
  aspect_ratio: z.string().default("1:1"),
  resolution: z.string().nullish(),
  image_size: z.string().nullish(),
  brand_style_prefix: z.string().default(""),
  image: z.union([assetRefSchema, z.string()]).nullish(),
  brand_kit_enabled: z.boolean().nullish(),
})
export type ImageGenConfig = z.infer<typeof imageGenConfigSchema>

export const videoGenConfigSchema = z.object({
  provider: z.string().default("google"),
  model: z.string().default("veo-3.1-generate-preview"),
  duration: z.coerce.number().int().min(1).max(60).default(8),
  aspect_ratio: z.string().default("16:9"),
  resolution: z.string().nullish(),
  brand_kit_enabled: z.boolean().nullish(),
})
export type VideoGenConfig = z.infer<typeof videoGenConfigSchema>

export const audioTTSConfigSchema = z.object({
  provider: z.string().default("openai"),
  model: z.string().default("tts-1"),
  voice: z.string().default("nova"),
  speed: z.coerce.number().min(0.25).max(4.0).default(1.0),
  stability: z.coerce.number().min(0).max(1).nullish(),
  similarity_boost: z.coerce.number().min(0).max(1).nullish(),
  style: z.coerce.number().min(0).max(1).nullish(),
  use_speaker_boost: z.boolean().nullish(),
})
export type AudioTTSConfig = z.infer<typeof audioTTSConfigSchema>

export const audioMusicConfigSchema = z.object({
  provider: z.string().default("replicate"),
  model: z.string().default("meta/musicgen"),
  duration: z.coerce.number().int().min(1).max(300).default(30),
  genre: z.string().default(""),
  brand_music_prefix: z.string().default(""),
})
export type AudioMusicConfig = z.infer<typeof audioMusicConfigSchema>

export const agentNodeConfigSchema = z.object({
  provider: z.string().default("google"),
  model: z.string().default("gemini-2.5-pro"),
  system_prompt: z.string().nullish(),
  tools: z.array(z.string()).default([]),
  max_iterations: z.coerce.number().int().min(1).max(100).default(10),
})
export type AgentNodeConfig = z.infer<typeof agentNodeConfigSchema>

// ── Input nodes ─────────────────────────────────────────────────────────────

export const textInputConfigSchema = z.object({
  text: z.string().default(""),
})

export const numberInputConfigSchema = z.object({
  value: z.coerce.number().default(0),
})

export const booleanInputConfigSchema = z.object({
  value: z.boolean().default(false),
})

export const fileInputConfigSchema = z.object({
  file: z.union([assetRefSchema, z.string()]).nullish(),
})

export const imageInputConfigSchema = z.object({
  image: z.union([assetRefSchema, z.string()]).nullish(),
})

export const textBuilderConfigSchema = z.object({
  template: z.string().default(""),
})

// ── Transform nodes ─────────────────────────────────────────────────────────

export const imageUpscalerConfigSchema = z.object({
  scale: z.enum(["2x", "4x"]).default("2x"),
})

export const imageConverterConfigSchema = z.object({
  format: z.enum(["png", "jpg", "webp", "gif", "svg"]).default("webp"),
})

export const imageCropperConfigSchema = z.object({
  mode: z.enum(["aspect_ratio", "coordinates"]).default("aspect_ratio"),
  aspect_ratio: z
    .enum(["1:1", "16:9", "4:3", "9:16", "3:2"])
    .default("1:1"),
  x: z.coerce.number().int().min(0).default(0),
  y: z.coerce.number().int().min(0).default(0),
  width: z.coerce.number().int().min(1).default(512),
  height: z.coerce.number().int().min(1).default(512),
})

export const imageResizerConfigSchema = z.object({
  width: z.coerce.number().int().min(1).max(16384).default(1024),
  height: z.coerce.number().int().min(1).max(16384).default(1024),
})

export const textFormatterConfigSchema = z.object({
  operation: z
    .enum(["trim", "uppercase", "lowercase", "truncate"])
    .default("trim"),
  max_length: z.coerce.number().int().min(1).default(280),
})

// ── Output nodes ────────────────────────────────────────────────────────────

export const videoAssemblyConfigSchema = z.object({
  format: z.enum(["mp4", "webm", "mov"]).default("mp4"),
})

// ── Registry ────────────────────────────────────────────────────────────────

const NODE_CONFIG_SCHEMAS: Record<string, z.ZodType> = {
  // Generation
  llm_text: llmTextConfigSchema,
  image_gen: imageGenConfigSchema,
  video_gen: videoGenConfigSchema,
  audio_tts: audioTTSConfigSchema,
  audio_music: audioMusicConfigSchema,
  agent_node: agentNodeConfigSchema,
  // Input
  text_input: textInputConfigSchema,
  number_input: numberInputConfigSchema,
  boolean_input: booleanInputConfigSchema,
  file_input: fileInputConfigSchema,
  image_input: imageInputConfigSchema,
  text_builder: textBuilderConfigSchema,
  // Transform
  image_upscaler: imageUpscalerConfigSchema,
  image_converter: imageConverterConfigSchema,
  image_cropper: imageCropperConfigSchema,
  image_resizer: imageResizerConfigSchema,
  text_formatter: textFormatterConfigSchema,
  // Output
  video_assembly: videoAssemblyConfigSchema,
}

/** Nodes with no config to validate */
const NO_CONFIG_NODES = new Set([
  "text_output",
  "image_output",
  "audio_output",
  "number_output",
  "background_remover",
  "approval_gate",
])

/**
 * Validate a node config object against its schema.
 * Returns human-readable error strings, or empty array if valid.
 */
export function validateNodeConfig(
  moduleType: string,
  config: Record<string, unknown>,
): string[] {
  if (NO_CONFIG_NODES.has(moduleType)) return []

  const schema = NODE_CONFIG_SCHEMAS[moduleType]
  if (!schema) return [] // Unknown module — don't block

  const result = schema.safeParse(config)
  if (result.success) return []

  return result.error.issues.map((issue) => {
    const path = issue.path.join(".")
    return path ? `${path}: ${issue.message}` : issue.message
  })
}

/**
 * Get the Zod schema for a module type (for advanced use).
 * Returns undefined for no-config or unknown modules.
 */
export function getConfigSchema(
  moduleType: string,
): z.ZodType | undefined {
  if (NO_CONFIG_NODES.has(moduleType)) return undefined
  return NODE_CONFIG_SCHEMAS[moduleType]
}

// ── Input (edge) validation ────────────────────────────────────────────────

const IMAGE_EXTENSIONS = new Set([
  ".png", ".jpg", ".jpeg", ".webp", ".gif", ".svg", ".bmp", ".tiff",
])
const VIDEO_EXTENSIONS = new Set([".mp4", ".webm", ".mov", ".avi", ".mkv"])
const AUDIO_EXTENSIONS = new Set([".mp3", ".wav", ".ogg", ".m4a", ".aac", ".flac"])

interface HandleDef {
  name: string
  type: string
  required: boolean
  handle: boolean
}

/**
 * Validate edge inputs against a module's expected input handles.
 * Requires the module definition's inputs array (from MODULE_REGISTRY).
 *
 * Checks:
 * 1. Required handle inputs are present and non-empty
 * 2. Media-type inputs (image/video/audio) are valid file paths
 * 3. Number inputs are actually numeric
 */
export function validateNodeInputs(
  handleDefs: HandleDef[],
  inputs: Record<string, unknown>,
): string[] {
  const errors: string[] = []

  for (const def of handleDefs) {
    if (!def.handle) continue

    const value = inputs[def.name]

    // Required check
    if (
      def.required &&
      (value == null || (typeof value === "string" && !value.trim()))
    ) {
      errors.push(
        `'${def.name}' input is required but ${value != null ? "empty" : "not connected"}`,
      )
      continue
    }

    if (value == null) continue

    // Type checks for media handles
    if (def.type === "image" && typeof value === "string") {
      const lower = value.toLowerCase()
      if (![...IMAGE_EXTENSIONS].some((ext) => lower.endsWith(ext))) {
        errors.push(
          `'${def.name}': expected an image file, got '${value.slice(0, 60)}'`,
        )
      }
    } else if (def.type === "video" && typeof value === "string") {
      const lower = value.toLowerCase()
      if (![...VIDEO_EXTENSIONS].some((ext) => lower.endsWith(ext))) {
        errors.push(
          `'${def.name}': expected a video file, got '${value.slice(0, 60)}'`,
        )
      }
    } else if (def.type === "audio" && typeof value === "string") {
      const lower = value.toLowerCase()
      if (![...AUDIO_EXTENSIONS].some((ext) => lower.endsWith(ext))) {
        errors.push(
          `'${def.name}': expected an audio file, got '${value.slice(0, 60)}'`,
        )
      }
    } else if (def.type === "number" && typeof value !== "number") {
      if (isNaN(Number(value))) {
        errors.push(`'${def.name}': expected a number, got '${value}'`)
      }
    }
  }

  return errors
}
